import * as z from "zod";

export const INVALID_API_RESPONSE = "invalid_api_response";

/**
 * Logout reached the browser but not the server: FastAPI answered non-OK, or
 * the call never arrived. The browser cookie is still cleared as a fail-safe,
 * so this code is the only signal left that the `auth_sessions` row may still
 * be alive (issue #268, S1 -- the handler used to swallow it and report 200).
 *
 * A machine-readable code rather than prose: route handlers must not put
 * untranslated English in front of a DE operator (AGENTS.md section 7).
 */
export const SESSION_REVOCATION_FAILED = "session_revocation_failed";

export class InvalidApiResponseError extends Error {
  readonly code = INVALID_API_RESPONSE;

  constructor(cause: z.ZodError) {
    super(INVALID_API_RESPONSE, { cause });
    this.name = "InvalidApiResponseError";
  }
}

/**
 * Parse domain JSON at the first trust boundary. The generated Zod schema is
 * derived from FastAPI's checked-in OpenAPI artifact, so missing/wrong fields
 * fail before UI code can silently reinterpret them.
 */
export async function parseJsonResponse<Schema extends z.ZodType>(
  response: Response,
  schema: Schema,
): Promise<z.output<Schema>> {
  const payload = (await response.json()) as unknown;
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    // Engineering evidence only. UI code catches the typed error and renders
    // localized generic copy; Zod paths/messages never become operator text.
    console.error(INVALID_API_RESPONSE, parsed.error.issues);
    throw new InvalidApiResponseError(parsed.error);
  }
  return parsed.data;
}

/** Read an opaque JSON payload that is forwarded without inspecting fields. */
export async function readJsonUnknown(response: Response): Promise<unknown> {
  return (await response.json()) as unknown;
}

/**
 * The two shapes a refused BetQ request arrives in.
 *
 * FastAPI's `HTTPException(detail=...)` carries either a coded object — what
 * every route that expects the UI to explain the failure sends, e.g.
 * `{"code": "header_mismatch", "reason": "..."}` — or a bare string, which is
 * also what `forwardCsvFormData` wraps a non-JSON upstream body in. Anything
 * else (an HTML error page, a proxy timeout) matches neither and is not an
 * error body this code can read.
 */
const zApiErrorEnvelope = z.object({
  detail: z.union([z.object({ code: z.string(), reason: z.string() }), z.string()]),
});

export type ApiError = {
  /** Machine code to key a localized sentence off, when the body carried one. */
  code: string | null;
  /** Untranslated backend text. Show it labelled as a technical detail, never as UI copy. */
  detail: string | null;
};

/**
 * Read a non-OK response as a validated error, at the trust boundary (#374).
 *
 * Components used to do this themselves with `readJsonUnknown` plus a hand-rolled
 * chain of `typeof` guards — duck-typing an untrusted body in UI code, which is
 * what QG003 restricts to audited paths. Validating here instead means the two
 * shapes above are a checked contract rather than an assumption, and a body that
 * is neither degrades to `{null, null}` so the caller renders its own generic
 * sentence.
 *
 * Never throws: this runs on the path where something has already gone wrong,
 * and a parse failure here must not replace the operator's error message with a
 * different one.
 */
export async function readApiError(response: Response): Promise<ApiError> {
  let payload: unknown;
  try {
    payload = (await response.json()) as unknown;
  } catch {
    return { code: null, detail: null };
  }

  const parsed = zApiErrorEnvelope.safeParse(payload);
  if (!parsed.success) {
    return { code: null, detail: null };
  }
  const { detail } = parsed.data;
  if (typeof detail === "string") {
    return { code: null, detail: detail.length > 0 ? detail : null };
  }
  return {
    code: detail.code.length > 0 ? detail.code : null,
    detail: detail.reason.length > 0 ? detail.reason : null,
  };
}
